import { useState } from "react";
import { Badge, Button, cn, type Tone } from "@/components/ui";
import { confidenceBand } from "@/lib/finance";
import type { ExtractedValue, VerificationStatus } from "@/lib/types";
import { useCase } from "@/components/case/case-context";

const STATUS_TONE: Record<VerificationStatus, Tone> = {
  verified: "positive",
  unverified: "caution",
  flagged: "negative",
};

const STATUS_MARK: Record<VerificationStatus, string> = {
  verified: "✓",
  unverified: "?",
  flagged: "⚠",
};

/**
 * One extracted value.
 *
 * Collapsed it shows the label, the figure and its state — the scanning view.
 * Expanded it exposes everything the spec requires of an extracted value:
 * original source, page/section, confidence, verification status, edit and
 * confirm. Expansion is inline rather than a modal so the analyst keeps the
 * surrounding figures in view while judging one of them.
 */
export function ExtractedRow({
  value,
  sourceName,
}: {
  value: ExtractedValue;
  sourceName: string;
}) {
  const { verify, correct, pending } = useCase();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(value.value);
  const [editing, setEditing] = useState(false);

  const band = confidenceBand(value.confidence);

  return (
    <div className={cn("border-b border-line last:border-0", expanded && "bg-surface-sunken")}>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent-soft/40"
      >
        <span
          aria-hidden
          className={cn(
            "w-3 shrink-0 text-xs transition-transform",
            expanded ? "rotate-90" : "",
            "text-ink-faint",
          )}
        >
          ›
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">{value.label}</span>
        <span className="numeric text-sm font-medium text-ink">{value.value}</span>
        <span
          aria-label={value.status}
          title={value.status}
          className={cn(
            "w-4 shrink-0 text-center text-sm",
            value.status === "verified"
              ? "text-positive"
              : value.status === "flagged"
                ? "text-negative"
                : "text-caution",
          )}
        >
          {STATUS_MARK[value.status]}
        </span>
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-line px-4 py-3 pl-10">
          <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
            <Detail label="Source" value={sourceName} />
            <Detail
              label="Location"
              value={
                [value.page ? `Page ${value.page}` : null, value.section].filter(Boolean).join(" · ") ||
                "Not specified"
              }
            />
            <Detail
              label="Confidence"
              value={
                <span className="flex items-center gap-1.5">
                  <span className="numeric">{Math.round(value.confidence * 100)}%</span>
                  <Badge tone={band === "high" ? "neutral" : band === "medium" ? "caution" : "negative"}>
                    {band}
                  </Badge>
                </span>
              }
            />
            <Detail
              label="Status"
              value={<Badge tone={STATUS_TONE[value.status]}>{value.status}</Badge>}
            />
          </dl>

          {value.note ? (
            <p className="rounded border border-line bg-surface px-3 py-2 text-xs leading-relaxed text-ink-muted">
              {value.note}
            </p>
          ) : null}

          {editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                aria-label={`Corrected value for ${value.label}`}
                className="numeric w-40 rounded-md border border-line-strong bg-surface px-2 py-1 text-sm"
              />
              <Button
                variant="primary"
                disabled={pending || draft.trim() === "" || draft === value.value}
                onClick={() => {
                  correct(value.id, draft);
                  setEditing(false);
                }}
              >
                Save correction
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(value.value);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={pending || value.status === "verified"}
                onClick={() => verify(value.id, "verified")}
              >
                Confirm
              </Button>
              <Button
                variant="secondary"
                disabled={pending || value.status === "flagged"}
                onClick={() => verify(value.id, "flagged")}
              >
                Flag
              </Button>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-ink-faint">{label}</dt>
      <dd className="min-w-0 text-ink">{value}</dd>
    </div>
  );
}
